/*eslint-disable*/
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useHistory } from "react-router-dom";
import Cookies from "js-cookie";
import {
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaChevronRight,
} from "react-icons/fa";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faFlask,
  faBan,
  faWrench,
  faQuestion,
} from "@fortawesome/free-solid-svg-icons";
import {
  formatCycleFileTime,
  formatNextWakeupDatetime,
  getRecentCyclesForDisplay,
} from "./installation/installationScheduleUtils";
import { getShortIdPart } from "../components/formatted/FormattedText";
import { useTranslation } from "react-i18next";
import { runCachedInstallationRequest } from "./installationRequestLimiter";
import { getInstallationCameraStatusLabel } from "./installation/installationConfigUtils";

const AppInstallationRow = ({ item, showScheduleDetails = false }) => {
  const { t } = useTranslation();
  const [fileList, setFileList] = useState([]);
  const [mostRecentLogDetails, setMostRecentLogDetails] = useState({});
  const history = useHistory();
  const rowRef = useRef(null);
  const hasStartedLoadingRef = useRef(false);
  const isMountedRef = useRef(false);

  const API = process.env.REACT_APP_API_BACKEND;
  const token = Cookies.get("userToken");
  const cameraId = useMemo(
    () => item._id.replace("_r", "").replace("_l", ""),
    [item._id],
  );

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const loadRowDetails = async () => {
    const response = await runCachedInstallationRequest(
      `azurecyclelogandimage:${cameraId}`,
      () =>
        axios.get(`${API}azurecyclelogandimage/${cameraId}`, {
          headers: { authorization: "Bearer " + token },
          params: { numberOfDaysBack: 1 },
        }),
    );

    if (!isMountedRef.current || response.status !== 200) return;

    const mostRecentFiles = getRecentCyclesForDisplay(response.data || [], 3);

    setFileList(mostRecentFiles);

    const mostRecentLogEntry = mostRecentFiles
      .filter((file) => file?.log)
      .reduce((latest, current) => {
        return new Date(latest.matchingCrontabDateTime) >
          new Date(current.matchingCrontabDateTime)
          ? latest
          : current;
      }, mostRecentFiles[0]);

    if (!mostRecentLogEntry?.log) return;

    const logDetailsResponse = await runCachedInstallationRequest(
      `akanthascameralogdetails:${cameraId}:${mostRecentLogEntry.name}`,
      () =>
        axios.get(`${API}akanthascameralogdetails/${cameraId}`, {
          headers: { authorization: "Bearer " + token },
          params: { filename: mostRecentLogEntry.name },
        }),
    );

    if (
      !isMountedRef.current ||
      logDetailsResponse.status !== 200 ||
      !logDetailsResponse.data
    )
      return;

    setMostRecentLogDetails(logDetailsResponse.data);
  };

  useEffect(() => {
    const element = rowRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (!first?.isIntersecting) return;
        if (hasStartedLoadingRef.current) return;
        hasStartedLoadingRef.current = true;
        loadRowDetails().catch(() => {});
      },
      { root: null, rootMargin: "0px 0px", threshold: 0.01 },
    );

    observer.observe(element);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraId, API, token]);

  const renderSensorCountIcon = (status) => {
    switch (status) {
      case "working":
        return (
          <FontAwesomeIcon
            icon={faCheckCircle}
            className="installation-row-status-icon installation-row-status-icon-working"
          />
        );
      case "test":
        return (
          <FontAwesomeIcon
            icon={faFlask}
            className="installation-row-status-icon installation-row-status-icon-test"
          />
        );
      case "broken":
        return (
          <FontAwesomeIcon
            icon={faWrench}
            className="installation-row-status-icon installation-row-status-icon-broken"
          />
        );
      case "dead":
        return (
          <FontAwesomeIcon
            icon={faBan}
            className="installation-row-status-icon installation-row-status-icon-dead"
          />
        );
      case "undefined":
        return (
          <FontAwesomeIcon
            icon={faQuestion}
            className="installation-row-status-icon installation-row-status-icon-undefined"
          />
        );
      default:
        return <span>?</span>;
    }
  };

  const openDetails = () => {
    history.push({
      pathname: `/app/installationdetails/${item._id}`,
      state: { cameraListItem: item },
    });
  };

  const sensorId = `ak_${getShortIdPart(item._id.split("ak_")[1])}`;

  return (
    <button
      ref={rowRef}
      type="button"
      className="installation-row-card"
      onClick={openDetails}
    >
      <div className="installation-row-card-body">
        <div className="installation-row-card-top">
          <div className="installation-row-card-id-block">
            <span className="installation-row-eyebrow">
              {t("installation_row_sensor_id")}
            </span>
            <span className="installation-row-card-id">{sensorId}</span>
          </div>
          <span className="installation-row-status-pill">
            {renderSensorCountIcon(item.status)}
            <span>{getInstallationCameraStatusLabel(t, item.status)}</span>
          </span>
        </div>

        {showScheduleDetails && (
          <div className="installation-row-cycles-block">
            <div className="installation-row-cycles-list">
              <div className="installation-row-cycle-row installation-row-cycle-row-next">
                <span className="installation-row-cycle-meta">
                  {t("installation_schedule_next_wakeup_label")}
                </span>
                <span className="installation-row-cycle-time">
                  {mostRecentLogDetails.nextWakeupDatetime
                    ? formatNextWakeupDatetime(
                        mostRecentLogDetails.nextWakeupDatetime,
                      )
                    : "—"}
                </span>
                <FaClock className="installation-row-cycle-icon next" />
              </div>
              {fileList.length === 0
                ? Array.from({ length: 3 }).map((_, index) => (
                    <div key={index} className="installation-row-cycle-row">
                      <span className="installation-row-cycle-meta">
                        {t("installation_loading")}
                      </span>
                      <span className="installation-row-cycle-time">…</span>
                      <FaClock className="installation-row-cycle-icon loading" />
                    </div>
                  ))
                : fileList.map((file, index) => (
                    <div
                      key={file.name || index}
                      className="installation-row-cycle-row"
                    >
                      <span className="installation-row-cycle-meta">
                        {file.log
                          ? t("installation_schedule_cycle_received")
                          : t("installation_schedule_cycle_missing")}
                      </span>
                      <span className="installation-row-cycle-time">
                        {formatCycleFileTime(file.name)}
                      </span>
                      {file.log ? (
                        <FaCheckCircle className="installation-row-cycle-icon received" />
                      ) : (
                        <FaTimesCircle className="installation-row-cycle-icon missing" />
                      )}
                    </div>
                  ))}
            </div>
          </div>
        )}
      </div>

      <span className="installation-row-chevron" aria-hidden>
        <FaChevronRight />
      </span>
    </button>
  );
};

export default AppInstallationRow;
